import React, { useState, useEffect, useMemo, useRef, useCallback, forwardRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import PropTypes from 'prop-types';
import { renderComponentField } from '../FormField';
import { DatePickerWrap } from './DatePickerElements';
import { parseISO, isValid, parse, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { applyMask } from '../maskUtils';

registerLocale('pt-BR', ptBR);

const DATE_FORMAT = 'dd/MM/yyyy';
const DATETIME_FORMAT = 'dd/MM/yyyy HH:mm';

const dateMask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
const dateTimeMask = [
  /\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, ':', /\d/, /\d/
];

const formatMaskedValue = (value, mask, dateFormatStr) => {
  if (!value) {
    return '';
  }

  if (value instanceof Date && isValid(value)) {
    return format(value, dateFormatStr, { locale: ptBR });
  }

  return applyMask(String(value), mask);
};

const tryParseMaskedDate = (rawValue, dateFormatStr) => {
  if (!rawValue || rawValue.length < dateFormatStr.length) {
    return null;
  }

  const parsed = parse(rawValue, dateFormatStr, new Date(), { locale: ptBR });
  return isValid(parsed) ? parsed : null;
};

const DateMaskedInput = forwardRef(({
  mask,
  value,
  dateFormatStr,
  onClick,
  onChange,
  onBlur,
  onFocus,
  onCommitText,
  onTextChange,
  placeholder,
  disabled,
  className,
  readOnly,
  ...props
}, ref) => {
  const [text, setText] = useState(() => formatMaskedValue(value, mask, dateFormatStr));
  const focusedRef = useRef(false);
  const textRef = useRef(text);

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  useEffect(() => {
    if (!focusedRef.current) {
      setText(formatMaskedValue(value, mask, dateFormatStr));
    }
  }, [value, mask, dateFormatStr]);

  return (
    <input
      {...props}
      ref={ref}
      value={text}
      readOnly={readOnly}
      disabled={disabled}
      placeholder={placeholder}
      className={className}
      onClick={onClick}
      onFocus={(event) => {
        focusedRef.current = true;
        if (onFocus) {
          onFocus(event);
        }
      }}
      onBlur={(event) => {
        focusedRef.current = false;
        const currentText = textRef.current;
        if (onCommitText) {
          onCommitText(currentText);
        }

        // Texto incompleto ao sair: volta ao valor já confirmado no form
        const parsed = tryParseMaskedDate(currentText, dateFormatStr);
        if (!currentText || parsed) {
          // vazio (já commitado null) ou data válida — useEffect sincroniza no próximo value
        } else {
          setText(formatMaskedValue(value, mask, dateFormatStr));
        }

        if (onBlur) {
          onBlur(event);
        }
      }}
      onChange={(event) => {
        const maskedValue = applyMask(event.target.value, mask);
        setText(maskedValue);
        if (onTextChange) {
          onTextChange(maskedValue);
        }
        onChange({
          ...event,
          target: {
            ...event.target,
            value: maskedValue
          }
        });
      }}
    />
  );
});

const toDate = val => {
  if (!val) return null;
  if (val instanceof Date) return isValid(val) ? val : null;

  if (typeof val === 'string') {
    const normalized =
      val.includes(' ') && /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}(:\d{2})?$/.test(val)
        ? val.replace(' ', 'T')
        : val;
    const d = parseISO(normalized);
    if (isValid(d)) {
      return d;
    }

    const asDate = parse(val, DATE_FORMAT, new Date(), { locale: ptBR });
    if (isValid(asDate)) {
      return asDate;
    }

    const asDateTime = parse(val, DATETIME_FORMAT, new Date(), { locale: ptBR });
    return isValid(asDateTime) ? asDateTime : null;
  }

  if (typeof val === 'number') {
    const d = new Date(val);
    return isValid(d) ? d : null;
  }

  return null;
};

export const DatePickerField = ({
  onChange,
  value,
  label,
  required,
  error,
  allowClear = true,
  readOnly,
  disabled,
  inputClassName,
  ...other
}) => {
  const [startDate, setStartDate] = useState(() => toDate(value));
  const typedTextRef = useRef('');

  const isDisabled = disabled || readOnly;
  const shouldAllowClear = allowClear && !readOnly;
  const isDateTime = other?.type === 'datetime' || other?.pickerType === 'datetime' || other?.showTimeSelect;
  const dateFormatStr = isDateTime ? DATETIME_FORMAT : DATE_FORMAT;
  const mask = isDateTime ? dateTimeMask : dateMask;

  const {
    popperPlacement = 'bottom-start',
    withPortal = false,
    ...datePickerOther
  } = other;

  useEffect(() => {
    const newDate = toDate(value);
    setStartDate(currentDate => {
      const currentTimestamp = currentDate ? currentDate.getTime() : null;
      const nextTimestamp = newDate ? newDate.getTime() : null;

      return currentTimestamp === nextTimestamp ? currentDate : newDate;
    });
  }, [value]);

  const memoizedStartDate = useMemo(() => {
    if (!startDate) return null;

    const timestamp = startDate.getTime();
    return Number.isNaN(timestamp) ? null : startDate;
  }, [startDate ? startDate.getTime() : null]);

  const commitDate = useCallback((date) => {
    if (isDisabled) return;

    setStartDate(date);

    if (onChange) {
      onChange(date || null);
    }
  }, [isDisabled, onChange]);

  const handleChange = (date) => {
    if (isDisabled) return;

    // react-datepicker pode enviar null no blur mesmo com texto válido digitado
    if (date === null && typedTextRef.current) {
      const parsedFromText = tryParseMaskedDate(typedTextRef.current, dateFormatStr);
      if (parsedFromText) {
        commitDate(parsedFromText);
        return;
      }
    }

    if (date === null) {
      typedTextRef.current = '';
    }

    commitDate(date);
  };

  const handleChangeRaw = (event) => {
    if (isDisabled) return;
    const rawValue = event?.target?.value ?? '';
    typedTextRef.current = rawValue;

    if (rawValue === '') {
      commitDate(null);
      return;
    }

    const parsed = tryParseMaskedDate(rawValue, dateFormatStr);
    if (parsed) {
      commitDate(parsed);
    }
  };

  const handleCommitText = useCallback((rawValue) => {
    if (isDisabled) return;

    typedTextRef.current = rawValue || '';

    if (!rawValue) {
      commitDate(null);
      return;
    }

    const parsed = tryParseMaskedDate(rawValue, dateFormatStr);
    if (parsed) {
      commitDate(parsed);
    }
  }, [commitDate, dateFormatStr, isDisabled]);

  const handleTextChange = useCallback((rawValue) => {
    typedTextRef.current = rawValue || '';
  }, []);

  return <div>
      {label && <div style={{
      fontSize: '12px',
      fontWeight: 500,
      color: '#333',
      marginBottom: '4px',
      display: 'block'
    }}>
          {label}
          {required && <span style={{
        color: '#dc3545',
        marginLeft: '4px'
      }}>*</span>}
      </div>}
      <DatePickerWrap>
        <DatePicker
          {...datePickerOther}
          selected={memoizedStartDate}
          onChange={handleChange}
          onChangeRaw={handleChangeRaw}
          type="text"
          locale="pt-BR"
          customInput={(
            <DateMaskedInput
              mask={mask}
              dateFormatStr={dateFormatStr}
              disabled={isDisabled}
              className={inputClassName}
              onCommitText={handleCommitText}
              onTextChange={handleTextChange}
            />
          )}
          dateFormat={dateFormatStr}
          showTimeSelect={isDateTime}
          timeFormat="HH:mm"
          timeIntervals={15}
          timeCaption="Hora"
          dropDownMode="select"
          popperPlacement={popperPlacement}
          withPortal={withPortal}
          isClearable={shouldAllowClear}
          placeholderText={isDateTime ? 'Selecione data e hora' : 'Selecione uma data'}
          allowSameDay={true}
          disabled={isDisabled}
        />
      </DatePickerWrap>
      {error && <div style={{
      color: '#dc3545',
      fontSize: '12px',
      marginTop: '4px'
    }}>
          {error}
        </div>}
    </div>;
};

DatePickerField.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),
  label: PropTypes.node,
  required: PropTypes.bool,
  error: PropTypes.string,
  allowClear: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool
};
export default renderComponentField(DatePickerField);
